import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormHelperText, InputLabel, OutlinedInput, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import BlockIcon from '@mui/icons-material/Block';
import { SUBMISSION_LIMITS } from '../../../model/ser/ser-enums';

interface CancelSubmissionDialogProps {
    open: boolean;
    title: string;
    // Resumo do que será cancelado (nº e título da remessa, ou lote e quantidade)
    summary: React.ReactNode;
    confirming: boolean;
    onClose: () => void;
    onConfirm: (reason: string) => void;
}

export const CANCEL_REASON_REQUIRED = 'Informe o motivo do cancelamento';

// Confirmação de cancelamento (remessa ou lote) com motivo obrigatório
export function CancelSubmissionDialog({ open, title, summary, confirming, onClose, onConfirm }: CancelSubmissionDialogProps) {
    const [reason, setReason] = useState('');
    const [touched, setTouched] = useState(false);
    const trimmed = reason.trim();
    const showError = touched && !trimmed;

    useEffect(() => {
        if (open) {
            setReason('');
            setTouched(false);
        }
    }, [open]);

    function handleConfirm() {
        setTouched(true);
        if (trimmed) onConfirm(trimmed);
    }

    return (
        <Dialog open={open} onClose={confirming ? undefined : onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <BlockIcon color="error" />
                {title}
            </DialogTitle>
            <DialogContent>
                <Alert severity="warning" icon={false} sx={{ mb: 2 }}>
                    {summary}
                    <Typography variant="body2" sx={{ mt: 0.5 }}>O cancelamento não pode ser desfeito. Para publicar depois, envie uma nova remessa.</Typography>
                </Alert>
                <InputLabel required>Motivo do cancelamento</InputLabel>
                <OutlinedInput
                    fullWidth
                    multiline
                    minRows={3}
                    autoFocus
                    value={reason}
                    error={showError}
                    disabled={confirming}
                    placeholder="Ex.: arquivo enviado com a versão errada"
                    inputProps={{ maxLength: SUBMISSION_LIMITS.cancelReasonMaxLength }}
                    onChange={(e) => setReason(e.target.value)}
                    onBlur={() => setTouched(true)}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <FormHelperText error>{showError ? CANCEL_REASON_REQUIRED : ' '}</FormHelperText>
                    <FormHelperText>{reason.length}/{SUBMISSION_LIMITS.cancelReasonMaxLength}</FormHelperText>
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button variant="outlined" onClick={onClose} disabled={confirming}>Voltar</Button>
                <LoadingButton variant="contained" color="error" startIcon={<BlockIcon />} loadingPosition="start" loading={confirming} disabled={!trimmed} onClick={handleConfirm}>
                    Confirmar cancelamento
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
}
